import { ALL_LOCATIONS } from "~/utils/Constants";
import { getOrgFromOrgIdString, getSiteFromOrgIdString } from "~/utils/Helper";
import type {
  AccessAssignment,
  GlobalCapability,
} from "~/utils/effectiveAccess";

/**
 * Client half of the server's grant-editing contract (`/admin/grants`): plain
 * JSON, no `ServerResponse` envelope.
 */
export const GRANTS_PATH = "/admin/grants";

export type GrantRung = "super-admin" | "super" | "admin" | "any";

/** A rung picked in the editor; "none" removes the location's row. */
export type RungChoice = GrantRung | "none";

export const RUNG_CHOICES: RungChoice[] = [
  "none",
  "super-admin",
  "super",
  "admin",
  "any",
];

export interface GrantUser {
  id: string;
  name: string;
  email?: string | null;
}

/** Display names repeat across test and real accounts; the email tells them apart. */
export const pilotLabelOf = ({ name, email }: GrantUser): string =>
  email && email !== name ? `${name} (${email})` : name;

export interface LocationGrantRow {
  org: string;
  location: string;
  capability: GrantRung;
  users: GrantUser[];
}

/** A place lending a feature to whoever holds it at the audience place. */
export interface FeatureShare {
  org: string;
  location: string;
  audience: GrantPlace;
}

export interface FeatureGrants {
  global: { capability: GlobalCapability } | null;
  rows: LocationGrantRow[];
  shares: FeatureShare[];
}

export interface LocationFeatureGrantRow {
  feature: string;
  capability: GlobalCapability;
  users: GrantUser[];
  /** The row is the global default, not one this location wrote. */
  isGlobal: boolean;
  globalCapability?: GlobalCapability;
  isConfigSuppressed: boolean;
  settingsPath?: string;
}

export interface LocationShare {
  feature: string;
  audience: GrantPlace;
}

export interface LocationGrants {
  rows: LocationFeatureGrantRow[];
  shares: LocationShare[];
}

export interface ShareWrite {
  feature: string;
  org: string;
  location: string;
  audience: GrantPlace;
}

export interface ShareWriteResult {
  before: ShareWrite | null;
  after: ShareWrite;
}

export interface ShareDeleteResult {
  before: ShareWrite;
}

/** The server refuses a share of an action: it follows its parent. */
export const SHAREABLE_LEVELS: readonly string[] = ["nav", "tab", "section"];

/** Audience places per feature, for the location view's badges. */
export const audiencesByFeature = (
  shares: LocationShare[],
): Map<string, GrantPlace[]> => {
  const byFeature = new Map<string, GrantPlace[]>();
  for (const share of shares) {
    byFeature.set(share.feature, [
      ...(byFeature.get(share.feature) ?? []),
      share.audience,
    ]);
  }
  for (const [feature, places] of byFeature) {
    byFeature.set(feature, [...places].sort(comparePlaces));
  }
  return byFeature;
};

export const placeLabelOf = (place: GrantPlace): string =>
  `${place.org} · ${place.location}`;

export interface GrantWrite {
  feature: string;
  org: string;
  location: string;
  capability: GrantRung;
  users: string[];
}

/** A row as the writes echo it: pilots by id only. */
export interface WrittenGrantRow {
  feature: string;
  org: string;
  location: string;
  capability: GrantRung;
  users: string[];
}

export interface GrantWriteResult {
  before: WrittenGrantRow | null;
  after: WrittenGrantRow;
  /** Pilots the server dropped because the rung already reaches them. */
  stripped: string[];
}

export interface GrantDeleteResult {
  before: WrittenGrantRow;
}

export interface GrantPlace {
  org: string;
  location: string;
}

const fold = (value: string): string => value.toLowerCase();

export const isSamePlace = (a: GrantPlace, b: GrantPlace): boolean =>
  fold(a.org) === fold(b.org) && fold(a.location) === fold(b.location);

export const placeKey = (place: GrantPlace): string =>
  `${fold(place.org)}/${fold(place.location)}`;

/** Every org location, in the order the org list returns them. */
const byText = (a: string, b: string) =>
  a.localeCompare(b, undefined, { sensitivity: "base" });

/** By org, then the org's AllLocations, then its sites. */
export const comparePlaces = (a: GrantPlace, b: GrantPlace): number =>
  byText(a.org, b.org) ||
  Number(b.location === ALL_LOCATIONS) - Number(a.location === ALL_LOCATIONS) ||
  byText(a.location, b.location);

export const placesOfOrgs = (orgs: { idString: string }[]): GrantPlace[] =>
  orgs
    .map((org) => ({
      org: getOrgFromOrgIdString(org.idString),
      location: getSiteFromOrgIdString(org.idString) ?? ALL_LOCATIONS,
    }))
    .sort(comparePlaces);

/** Places as select groups, one per org, in place order. */
export const placeGroupsOf = (
  places: GrantPlace[],
): { group: string; items: { value: string; label: string }[] }[] => {
  const groups = new Map<string, { value: string; label: string }[]>();
  for (const place of [...places].sort(comparePlaces)) {
    const items = groups.get(place.org) ?? [];
    items.push({
      value: placeKey(place),
      label: placeLabelOf(place),
    });
    groups.set(place.org, items);
  }
  return [...groups].map(([group, items]) => ({ group, items }));
};

export const sortUsersByLabel = (users: GrantUser[]): GrantUser[] =>
  [...users].sort((a, b) => byText(pilotLabelOf(a), pilotLabelOf(b)));

export const isGrantRung = (
  capability: GlobalCapability | undefined,
): capability is GrantRung =>
  capability === "super-admin" ||
  capability === "super" ||
  capability === "admin" ||
  capability === "any";

export interface FeatureLocationRow {
  place: GrantPlace;
  /** The row this location wrote, if any. */
  row: LocationGrantRow | undefined;
  /** What reaches this location: its own row, else the global default. */
  source: "location" | "global" | "none";
  capability: GlobalCapability | undefined;
}

/** One row per location: its own grant overrides the global default. */
export const featureLocationRowsOf = (
  places: GrantPlace[],
  grants: FeatureGrants | undefined,
): FeatureLocationRow[] =>
  places.map((place) => {
    const row = grants?.rows.find((entry) => isSamePlace(entry, place));
    if (row) {
      return { place, row, source: "location", capability: row.capability };
    }
    const global = grants?.global ?? null;
    return {
      place,
      row: undefined,
      source: global ? "global" : "none",
      capability: global?.capability,
    };
  });

export interface PilotCandidateUser {
  _id: string;
  displayName?: string;
  email?: string;
  isValid?: boolean;
  assignments?: AccessAssignment[];
  super?: string;
}

const ALL_LOCATIONS_FOLDED = ALL_LOCATIONS.toLowerCase();

/** The org's AllLocations assignment covers each of its locations. */
export const assignmentCovers = (
  assignment: GrantPlace,
  place: GrantPlace,
): boolean =>
  fold(assignment.org) === fold(place.org) &&
  (fold(assignment.location) === fold(place.location) ||
    fold(assignment.location) === ALL_LOCATIONS_FOLDED);

/**
 * Pilots must hold an assignment covering the location, unless they are a
 * super user, and a super admin is never a pilot: the server refuses both.
 * Someone the rung already reaches is still offered; the server strips them
 * and says so.
 */
export const pilotCandidatesOf = (
  users: PilotCandidateUser[],
  place: GrantPlace,
): GrantUser[] =>
  users
    .filter((user) =>
      user.super
        ? user.super === "super-user"
        : (user.assignments ?? []).some((assignment) =>
            assignmentCovers(assignment, place),
          ),
    )
    .map((user) => ({
      id: user._id,
      name: user.displayName ?? user.email ?? user._id,
      email: user.email ?? null,
    }));

export type GrantEdit =
  | { kind: "put"; write: GrantWrite }
  | { kind: "delete"; place: GrantPlace; feature: string }
  | { kind: "none" };

/**
 * The write a rung pick makes. "none" deletes the location's row, which with
 * a global default reverts the location to it.
 */
export const rungEditOf = (
  feature: string,
  place: GrantPlace,
  row: { users: GrantUser[] } | undefined,
  choice: RungChoice,
): GrantEdit => {
  if (choice === "none") {
    return row ? { kind: "delete", place, feature } : { kind: "none" };
  }
  return {
    kind: "put",
    write: {
      feature,
      org: place.org,
      location: place.location,
      capability: choice,
      users: (row?.users ?? []).map((user) => user.id),
    },
  };
};

/**
 * The write a pilot change makes. A location still on the global default
 * writes its own row at the default's rung to carry the pilots.
 */
export const pilotEditOf = (
  feature: string,
  place: GrantPlace,
  capability: GlobalCapability | undefined,
  users: string[],
): GrantEdit =>
  isGrantRung(capability)
    ? {
        kind: "put",
        write: {
          feature,
          org: place.org,
          location: place.location,
          capability,
          users,
        },
      }
    : { kind: "none" };

export const strippedNamesOf = (
  stripped: string[],
  known: GrantUser[],
): string[] =>
  stripped.map((id) => known.find((user) => user.id === id)?.name ?? id);

export type AccessView = "feature" | "location";

/**
 * `feature` is the picked feature in the feature view and the highlighted row
 * in the location view; both views share it so switching keeps the selection.
 */
export interface AccessSearch {
  view: AccessView;
  /** The feature the feature view is showing. */
  feature?: string;
  org?: string;
  location?: string;
  /** A row the location view highlights, set by a link into it. */
  highlight?: string;
  /** Both views hide rows the location has not written. */
  rowsOnly?: boolean;
}

const optionalString = (value: unknown): string | undefined =>
  typeof value === "string" && value.length > 0 ? value : undefined;

export const parseAccessSearch = (
  search: Record<string, unknown>,
): AccessSearch => ({
  view: search.view === "location" ? "location" : "feature",
  feature: optionalString(search.feature),
  org: optionalString(search.org),
  location: optionalString(search.location),
  highlight: optionalString(search.highlight),
  rowsOnly: search.rowsOnly === true || search.rowsOnly === "true" || undefined,
});

export const ACCESS_TAB_PATH = "/administration/access";

export const locationViewSearchOf = (
  feature: string,
  place: GrantPlace,
): AccessSearch => ({
  view: "location",
  org: place.org,
  location: place.location,
  highlight: feature,
});
